/**
 * Search Utilities
 * Functions for text highlighting and search result processing
 */

import React from 'react';

export interface HighlightedText {
  text: string;
  isHighlight: boolean;
}

/**
 * Split text into parts based on search query matches
 * Returns array of text segments with highlight flags
 */
export function splitTextForHighlight(
  text: string,
  query: string
): HighlightedText[] {
  if (!query || !text) {
    return [{ text, isHighlight: false }];
  }

  // Escape special regex characters in the query
  const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  // Create case-insensitive regex to find all matches
  const regex = new RegExp(`(${escapedQuery})`, 'gi');
  const parts = text.split(regex);

  return parts
    .filter((part) => part.length > 0)
    .map((part) => ({
      text: part,
      isHighlight: part.toLowerCase() === query.toLowerCase(),
    }));
}

/**
 * Find and extract a snippet of text around the first match of the query
 * Returns the snippet with surrounding context
 */
export function getSearchSnippet(
  content: string,
  query: string,
  maxLength: number = 200
): string | null {
  if (!query || !content) {
    return null;
  }

  const lowerContent = content.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const matchIndex = lowerContent.indexOf(lowerQuery);

  if (matchIndex === -1) {
    return null;
  }

  // Calculate start and end positions for the snippet
  const contextLength = Math.floor((maxLength - query.length) / 2);
  let start = Math.max(0, matchIndex - contextLength);
  let end = Math.min(content.length, matchIndex + query.length + contextLength);

  // Adjust to word boundaries if possible
  if (start > 0) {
    const spaceIndex = content.lastIndexOf(' ', start + 10);
    if (spaceIndex > start - 20) {
      start = spaceIndex + 1;
    }
  }

  if (end < content.length) {
    const spaceIndex = content.indexOf(' ', end - 10);
    if (spaceIndex !== -1 && spaceIndex < end + 20) {
      end = spaceIndex;
    }
  }

  let snippet = content.slice(start, end);

  // Add ellipsis if truncated
  if (start > 0) {
    snippet = '...' + snippet;
  }
  if (end < content.length) {
    snippet = snippet + '...';
  }

  return snippet;
}

/**
 * Create React elements with highlighted text
 * Returns an array of React elements (spans) with highlighted portions styled
 */
export function createHighlightedElements(
  text: string,
  query: string,
  highlightClassName: string = 'bg-yellow-200 text-yellow-900 px-0.5 rounded'
): React.ReactNode[] {
  const parts = splitTextForHighlight(text, query);

  return parts.map((part, index) =>
    React.createElement(
      'span',
      {
        key: index,
        className: part.isHighlight ? highlightClassName : undefined,
      },
      part.text
    )
  );
}

/**
 * Check if a document matches a search query
 * Searches in title, content, and tags
 */
export function documentMatchesQuery(
  document: { title?: string; content?: string; tags?: string[] },
  query: string
): boolean {
  if (!query) return true;

  const lowerQuery = query.toLowerCase();

  // Check title
  if (document.title?.toLowerCase().includes(lowerQuery)) {
    return true;
  }

  // Check content
  if (document.content?.toLowerCase().includes(lowerQuery)) {
    return true;
  }

  // Check tags
  if (document.tags?.some((tag) => tag.toLowerCase().includes(lowerQuery))) {
    return true;
  }

  return false;
}

/**
 * Get where the match was found in the document
 */
export function getMatchLocation(
  document: { title?: string; content?: string; tags?: string[] },
  query: string
): 'title' | 'content' | 'tags' | null {
  if (!query) return null;

  const lowerQuery = query.toLowerCase();

  if (document.title?.toLowerCase().includes(lowerQuery)) {
    return 'title';
  }

  if (document.content?.toLowerCase().includes(lowerQuery)) {
    return 'content';
  }

  if (document.tags?.some((tag) => tag.toLowerCase().includes(lowerQuery))) {
    return 'tags';
  }

  return null;
}
